import { ChevronUp, Search } from "lucide-react";
import React, { useCallback, useMemo, useRef } from "react";
import type { ComboBoxRenderProps, Key } from "react-aria-components";
import {
  Button,
  ComboBox,
  Input,
  ListBox,
  ListBoxItem,
  ListLayout,
  Popover,
  Label as RACLabel,
  Virtualizer,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Checkbox } from "./Checkbox";
import { FocusableText } from "./FocusableText";
import { Tooltip } from "./Tooltip";

type Option = {
  label: string;
  value: string | number;
  count?: string | number;
  tooltipText?: string;
  disabled?: boolean;
};

type Props = {
  className?: string;
  placeholder?: string;
  label?: string;
  description?: string;
  trailingIcon?: React.ReactNode;
  selectedOptions: Option | Option[];
  setSelectedOptions: (options: Option | Option[]) => void;
  options: Option[];
  errorMessage?: string;
  multiple?: boolean;
  isDisabled?: boolean;
  filter?: (option: Option, query: string) => boolean;
  search?: boolean;
  disabledKeys?: Set<string | number>;
};

export function TagField({
  className,
  options,
  selectedOptions,
  setSelectedOptions,
  placeholder,
  label,
  description,
  trailingIcon = <Search className="size-3.5 cursor-pointer xl:size-5" />,
  errorMessage,
  multiple = true,
  isDisabled = false,
  filter,
  disabledKeys = new Set(),
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Fast lookup map for converting RAC keys back to Option objects
  const allOptionsMap = useMemo(
    () => new Map(options.map((o) => [o.value, o])),
    [options],
  );

  // Build a defaultFilter that delegates to the custom filter prop if provided
  const defaultFilter = useCallback(
    (textValue: string, inputValue: string) => {
      if (filter) {
        // Find the option by textValue (label) to pass the full option to the custom filter
        const option = options.find((o) => o.label === textValue);
        return option ? filter(option, inputValue) : false;
      }
      return textValue.toLowerCase().includes(inputValue.toLowerCase());
    },
    [filter, options],
  );

  // Convert selectedOptions to RAC's key-based value (one variant per selection mode)
  const racValueMultiple = useMemo<Key[]>(
    () =>
      Array.isArray(selectedOptions) ? selectedOptions.map((o) => o.value) : [],
    [selectedOptions],
  );

  const racValueSingle = useMemo<Key | null>(
    () =>
      Array.isArray(selectedOptions)
        ? (selectedOptions.at(0)?.value ?? null)
        : selectedOptions.value,
    [selectedOptions],
  );

  const handleChangeMultiple = useCallback(
    (keys: Key[]) => {
      const enabledKeys = keys.filter((k) => !disabledKeys.has(k));
      const existingDisabledOptions = Array.isArray(selectedOptions)
        ? selectedOptions.filter((opt) => disabledKeys.has(opt.value))
        : [];
      const newOptions = enabledKeys
        .map((k) => allOptionsMap.get(k))
        .filter((o): o is Option => o !== undefined);
      setSelectedOptions([...existingDisabledOptions, ...newOptions]);
    },
    [allOptionsMap, disabledKeys, selectedOptions, setSelectedOptions],
  );

  const handleChangeSingle = useCallback(
    (key: Key | null) => {
      if (key !== null) {
        const opt = allOptionsMap.get(key);
        if (opt && !disabledKeys.has(opt.value)) {
          setSelectedOptions(opt);
        }
      }
    },
    [allOptionsMap, disabledKeys, setSelectedOptions],
  );

  const comboBoxContent = ({ isOpen: comboOpen }: ComboBoxRenderProps) => (
    <>
      {label && (
        <RACLabel className="text-theme-on-background-variant w-fit cursor-default text-sm font-medium">
          {label}
        </RACLabel>
      )}
      <div className="relative flex w-full">
        <Input
          aria-label={label}
          ref={inputRef}
          autoComplete="off"
          data-1p-ignore
          data-bwignore
          placeholder={placeholder}
          className="peer text-theme-on-background border-theme-outline-variant placeholder:text-theme-on-background-variant disabled:border-theme-outline-variant/50 w-full grow rounded-sm border bg-transparent py-3.5 pr-8 pl-3.5 text-sm font-bold outline-0 placeholder:font-medium focus:outline-hidden xl:py-7 xl:pr-16 xl:pl-7"
        />
        <Button className="group text-theme-on-background absolute top-1/2 right-3.5 -translate-y-1/2 peer-disabled:opacity-50 xl:right-7">
          {comboOpen ? (
            <ChevronUp className="size-3.5 cursor-pointer xl:size-5" />
          ) : (
            trailingIcon
          )}
        </Button>
      </div>
      <Popover
        placement="bottom"
        offset={8}
        className="colors-glass backdrop-blur-glass min-h-1 max-w-dvw min-w-(--trigger-width) rounded-md text-[11px]/[16px]"
      >
        <Virtualizer
          layout={ListLayout}
          layoutOptions={{ estimatedRowHeight: 44, padding: 12 }}
        >
          <ListBox<Option> className="block max-h-[inherit] min-h-11 overflow-y-auto outline-none">
            {(option) => {
              const isOptionDisabled = disabledKeys.has(option.value);
              return (
                <ListBoxItem
                  id={option.value}
                  textValue={option.label}
                  isDisabled={isOptionDisabled}
                  aria-label={option.label}
                  className={twMerge(
                    "group flex h-11 w-full cursor-pointer items-center gap-x-3 rounded-md px-4 font-medium text-nowrap transition-[background-color] duration-200",
                    isOptionDisabled
                      ? "bg-theme-on-background-hover/50 cursor-not-allowed opacity-50"
                      : "hover:bg-theme-on-background-hover data-focused:bg-theme-on-background-hover",
                    !multiple && "selected:text-theme-active",
                  )}
                >
                  {({ isSelected }) => (
                    <>
                      {multiple && (
                        <Checkbox
                          value={String(option.value)}
                          size="xs"
                          className="pointer-events-none"
                          isSelected={isSelected}
                          isReadOnly
                        />
                      )}
                      <FocusableText
                        className="w-0 shrink grow truncate"
                        text={option.label}
                        tooltipText={option.tooltipText}
                        tooltipPlacement="right"
                        tooltipDelay={1500}
                      >
                        {option.label}
                      </FocusableText>
                      {option.count && (
                        <div className={isOptionDisabled ? "opacity-60" : ""}>
                          {option.count}
                        </div>
                      )}
                    </>
                  )}
                </ListBoxItem>
              );
            }}
          </ListBox>
        </Virtualizer>
      </Popover>
      {errorMessage && (
        <div className="text-[11px] text-red-600">{errorMessage}</div>
      )}
    </>
  );

  const sharedComboBoxProps = {
    defaultItems: options,
    defaultFilter,
    disabledKeys,
    isDisabled,
    menuTrigger: "focus" as const,
    className: "w-full",
  };

  return (
    <div
      className={twMerge("flex w-full flex-col items-start gap-y-2", className)}
    >
      <Tooltip tooltipText={description}>
        {multiple ? (
          <ComboBox<Option, "multiple">
            selectionMode="multiple"
            value={racValueMultiple}
            onChange={handleChangeMultiple}
            aria-label={label ?? placeholder}
            {...sharedComboBoxProps}
          >
            {comboBoxContent}
          </ComboBox>
        ) : (
          <ComboBox<Option, "single">
            selectionMode="single"
            value={racValueSingle}
            onChange={handleChangeSingle}
            aria-label={label ?? placeholder}
            {...sharedComboBoxProps}
          >
            {comboBoxContent}
          </ComboBox>
        )}
      </Tooltip>
    </div>
  );
}
